/**
 * ThinkingIndicator - Animated shimmer effect for AI processing.
 *
 * Shows processing phases with elapsed time counter for long operations.
 * Replaces the static "Thinking..." spinner with a more engaging visual.
 */

import { useState, useEffect } from 'react'
import { Brain, Sparkles, Cog, MessageSquare } from 'lucide-react'

type ProcessingPhase = 'analyzing' | 'planning' | 'executing' | 'responding'

interface ThinkingIndicatorProps {
  status?: string | null
  startTime?: number
}

const PHASE_CONFIG: Record<ProcessingPhase, { icon: typeof Brain; label: string }> = {
  analyzing: { icon: Brain, label: 'Analyzing' },
  planning: { icon: Cog, label: 'Planning' },
  executing: { icon: Sparkles, label: 'Executing' },
  responding: { icon: MessageSquare, label: 'Responding' },
}

function inferPhase(status: string | null): ProcessingPhase {
  if (!status) return 'analyzing'
  const lower = status.toLowerCase()
  if (lower.includes('executing') || lower.includes('running') || lower.includes('tool')) {
    return 'executing'
  }
  if (lower.includes('planning') || lower.includes('thinking')) {
    return 'planning'
  }
  if (lower.includes('responding') || lower.includes('generating')) {
    return 'responding'
  }
  return 'analyzing'
}

export function ThinkingIndicator({ status, startTime }: ThinkingIndicatorProps) {
  const [elapsed, setElapsed] = useState(0)
  const [effectiveStartTime] = useState(() => startTime ?? Date.now())

  // Update elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - effectiveStartTime) / 1000))
    }, 1000)

    return () => clearInterval(interval)
  }, [effectiveStartTime])

  const phase = inferPhase(status ?? null)
  const config = PHASE_CONFIG[phase]
  const Icon = config.icon

  return (
    <div className="thinking-shimmer rounded-xl px-4 py-3 bg-surface/50 border border-surface-border">
      <div className="flex items-center gap-3">
        {/* Animated icon with pulse glow */}
        <div className="relative flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-accent animate-pulse" />
          </div>
          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-lg animate-pulse-glow" />
        </div>

        {/* Status text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-text">
              {status || config.label}
            </span>
            {/* Streaming dots */}
            <span className="streaming-cursor text-accent" />
          </div>
          {elapsed > 2 && (
            <div className="text-xs text-text-muted mt-0.5">
              {formatElapsed(elapsed)}
            </div>
          )}
        </div>

        {/* Phase indicator dots */}
        <div className="flex gap-1">
          {(['analyzing', 'planning', 'executing', 'responding'] as ProcessingPhase[]).map(
            (p, i) => (
              <div
                key={p}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i <= ['analyzing', 'planning', 'executing', 'responding'].indexOf(phase)
                    ? 'bg-accent'
                    : 'bg-white/20'
                }`}
              />
            )
          )}
        </div>
      </div>
    </div>
  )
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

export default ThinkingIndicator
